'use client';

import React from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { Video } from '@/components/LeftTextRightImageAndVideo';
import Link from 'next/link';
import { SlArrowRightCircle } from 'react-icons/sl';
import { PortableTextWrapper } from './PortableTextWrapper';
import { XIcon } from 'lucide-react';
import { useMediaQuery } from 'usehooks-ts';
import { MOBILE_SCREEN_SIZE } from '@/utils/constants';

type ModalProps = {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  video: Video;
};

const isYoutubeVideo = (videoUrl: string): boolean => {
  const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/i;
  return youtubeRegex.test(videoUrl);
};

// A simple modal component which can be shown/hidden with a boolean and a function
// Because of the setIsModalOpen function, you can't use it in a server component.
const VideoModal = ({ isModalOpen, setIsModalOpen, video }: ModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${MOBILE_SCREEN_SIZE}px)`);
  return (
    <Dialog
      open={isModalOpen}
      onClose={setIsModalOpen}
      className="relative z-40 overflow-hidden w-full min-w-max"
    >
      <DialogBackdrop
        transition
        className="z-40 fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-40 w-screen overflow-y-auto xl:min-h-[400px]">
        <div className="flex min-w-full sm:h-full items-end justify-center p-0 sm:items-center sm:p-12">
          <DialogPanel
            transition
            className="relative overflow-y-auto h-[100vh] md:h-auto sm:max-h-[95vh] rounded transform bg-black shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-fit sm:p-0 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <h4 className="text-white pt-4 ps-6 pb-4 text-lg font-semibold">
              {video?.title}
            </h4>
            <div className="flex flex-col lg:flex-row justify-between pr-8 flex-1">
              <div className="lg:min-w-[72%] pl-6 pb-6">
                {isYoutubeVideo(video.videoUrl) ? (
                  <iframe
                    className="w-full"
                    height="620"
                    src={video.videoUrl}
                    title="YouTube video player"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                ) : (
                  <video
                    className="object-cover min-h-[450px]"
                    controls
                    autoPlay
                    loop
                  >
                    <source src={video.videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
              {!isMobile && video.description && (
                <div className="lg:min-w-[28%] flex flex-col justify-between mb-1 pt-2 pb-6 pr-6 pl-6 text-white overflow-y-auto">
                  <div className="-mt-2 text-sm">
                    <PortableTextWrapper
                      value={video.description}
                      textColor="white"
                    />
                  </div>
                  <div>
                    {video.learnMore && video.learnMore.external && (
                      <Link
                        href={video.learnMore.external}
                        className="cursor-pointer btn btn-primary btn-sm lg:btn-md w-full flex flex-row justify-between align-baseline !px-3"
                      >
                        {video.learnMore.label}
                        <SlArrowRightCircle className="mt-1" size={18} />
                      </Link>
                    )}
                    {video.learnMore && video.learnMore.internal && (
                      <Link
                        href={
                          video.learnMore.internal.ref?.metadata?.slug?.current
                        }
                        className="cursor-pointer btn btn-primary btn-sm lg:btn-md w-full flex flex-row justify-between align-baseline !px-3"
                      >
                        {video.learnMore.label}
                        <SlArrowRightCircle className="mt-1" size={18} />
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute right-6 top-6 p-3 text-zinc-50 cursor-pointer dark:bg-gray-900 bg-gray-400 hover:bg-gray-500 rounded-full dark:hover:bg-gray-800"
              type="button"
              aria-label="Close video modal"
            >
              <XIcon size={24} />
            </button>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default VideoModal;
